// src/lib/announcementsService.js
// Firestore layer for the Announcements page (news posts and tournament cards).
//
// Mirrors playersService: the content used to be hardcoded in the page, so any
// change needed a code push. It now lives in Firestore and is managed from the
// admin panel, with the original entries kept as a fallback and import source.

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { STATIC_ANNOUNCEMENTS } from "../data/announcementsData";
import { deletePlayerImage } from "./imageUpload";

export const ANNOUNCEMENTS_COL = collection(db, "announcements");

export const ANNOUNCEMENT_TYPES = [
  { value: "news", label: "News post" },
  { value: "tournament", label: "Tournament" },
];

// ─── Writes ──────────────────────────────────────────────────────────────────

/**
 * Shape a form object into a Firestore document.
 *
 * Careful: `status` here is the PUBLISH state (draft/published), same as the
 * roster. Whether a tournament is upcoming or completed is `eventStatus` —
 * the two must never share a field.
 */
function toDoc(form) {
  const type = form.type === "tournament" ? "tournament" : "news";
  return {
    type,
    title: (form.title || "").trim(),
    excerpt: (form.excerpt || "").trim(),
    // News-only fields (kept empty on tournaments rather than dropped, so a
    // type never has fields another type can't read)
    body: type === "news" ? (form.body || "").trim() : "",
    author: type === "news" ? (form.author || "").trim() || "Prime Valor" : "",
    category: type === "news" ? (form.category || "").trim() || "Announcement" : "",
    // Tournament-only fields
    eventStatus:
      type === "tournament"
        ? form.eventStatus === "completed"
          ? "completed"
          : "upcoming"
        : "",
    prizePool: type === "tournament" ? (form.prizePool || "").trim() || "TBA" : "",
    format: type === "tournament" ? (form.format || "").trim() : "",
    game: type === "tournament" ? (form.game || "").trim() : "",
    // Shared
    date: (form.date || "").trim(),
    imageUrl: form.imageUrl || "",
    imagePath: form.imagePath || "",
    legacyImageKey: form.legacyImageKey || "",
    featured: type === "news" && Boolean(form.featured),
    status: form.status === "published" ? "published" : "draft",
    publishAt: form.publishAt ? Timestamp.fromMillis(form.publishAt) : null,
    wasPublished: Boolean(form.wasPublished) || form.status === "published",
    sortOrder: Number(form.sortOrder) || 0,
  };
}

/**
 * Only one announcement carries the big featured spot at the top of the page.
 * Marking a new one featured quietly un-features whichever had it before, so
 * the page can never end up with two heroes fighting for the slot.
 */
async function clearOtherFeatured(keepId) {
  const snap = await getDocs(ANNOUNCEMENTS_COL);
  const others = snap.docs.filter((d) => d.id !== keepId && d.data().featured);
  if (others.length === 0) return;
  const batch = writeBatch(db);
  others.forEach((d) => batch.update(d.ref, { featured: false, updatedAt: serverTimestamp() }));
  await batch.commit();
}

export async function createAnnouncement(form) {
  const data = toDoc(form);
  // New items go to the end of their section rather than position 0, which
  // would shove everything else down.
  if (!data.sortOrder) data.sortOrder = Date.now();
  const ref = await addDoc(ANNOUNCEMENTS_COL, {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  if (data.featured) await clearOtherFeatured(ref.id);
  return ref.id;
}

export async function updateAnnouncement(id, form) {
  const data = toDoc(form);
  await updateDoc(doc(ANNOUNCEMENTS_COL, id), {
    ...data,
    updatedAt: serverTimestamp(),
  });
  if (data.featured) await clearOtherFeatured(id);
}

/** Delete an announcement and its uploaded image. */
export async function removeAnnouncement(item) {
  if (item.imagePath) await deletePlayerImage(item.imagePath); // generic path delete
  await deleteDoc(doc(ANNOUNCEMENTS_COL, item.id));
}

/**
 * Persist a new running order within one type (news or tournaments).
 * Renumbers just that section, so reordering news can't disturb tournaments.
 */
export async function reorderAnnouncements(orderedItems) {
  const batch = writeBatch(db);
  orderedItems.forEach((item, index) => {
    batch.update(doc(ANNOUNCEMENTS_COL, item.id), {
      sortOrder: index,
      updatedAt: serverTimestamp(),
    });
  });
  await batch.commit();
}

// ─── Reads ───────────────────────────────────────────────────────────────────

/** Live subscription to every announcement, including drafts. For the admin. */
export function subscribeToAllAnnouncements(callback, onError) {
  return onSnapshot(
    ANNOUNCEMENTS_COL,
    (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort(
        (a, b) =>
          (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
          (a.title || "").localeCompare(b.title || "")
      );
      callback(list);
    },
    onError
  );
}

// ─── One-time migration ──────────────────────────────────────────────────────

/**
 * Copy the original hardcoded announcements into Firestore.
 * Images are NOT re-uploaded — the originals keep using the bundled files,
 * matched by legacyImageKey. Refuses to run twice.
 */
export async function seedAnnouncementsFromStaticData() {
  const existing = await getDocs(ANNOUNCEMENTS_COL);
  if (!existing.empty) {
    throw new Error(
      `Announcements already imported (${existing.size} items). Delete them first if you need to re-import.`
    );
  }
  const batch = writeBatch(db);
  STATIC_ANNOUNCEMENTS.forEach((item, index) => {
    batch.set(doc(ANNOUNCEMENTS_COL), {
      ...toDoc({ ...item, status: "published", sortOrder: index }),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });
  await batch.commit();
  return STATIC_ANNOUNCEMENTS.length;
}
